/**
 * In-memory stand-in for api-service's personalized distribution routes. It speaks the wire
 * contract exactly (distributions.types.ts) and behaves like the backend: writes answer 202 and
 * report `pending` on the first poll of /edits, then `done`; runs move queued → running → done
 * across reads; failures reject with `MockDomainError` so getApiErrorMessage reads them the
 * same way as live errors. Any story id works (it is seeded on first use), except the two below.
 */
import { MOCK_PRESENTATION_THEMES } from "../presentationThemes/presentationThemesApi.mock";
import { andList, pluralize, slugOf } from "./distributionFormat";
import {
  computeCoverage,
  emailDomainOf,
  includedValues,
  summarizeRecipients,
  toRecipientApi,
  uncoveredSentence,
  type RecipientRuleContext,
} from "./distributionRecipientRules";
import { nextSendIso, previewSchedule } from "./distributionScheduleMath";
import { findUnknownTokens, localTokenCatalog, usedExtraTokens } from "./distributionTokens";
import {
  MOCK_AUDIENCE_CLEARANCE,
  MOCK_BUILT_IN_TEMPLATES,
  MOCK_CATEGORY_ROWS,
  MOCK_DEFAULT_BRAND,
  MOCK_DEFAULT_MESSAGE,
  MOCK_CALLER_CLEARANCE,
  MOCK_FIELDS,
  MOCK_MEMBER_EMAILS,
  MOCK_MODEL_ATTRIBUTES,
  MOCK_MODEL_PEOPLE,
  MOCK_NEW_DISTRIBUTION_MESSAGE,
  MOCK_REGIONAL_PACK_ROWS,
  MOCK_SLACK_CHANNELS_WITHOUT_APP,
  MOCK_SPLIT_FIELDS,
  MOCK_STORE_ROWS,
  MOCK_STORY_TITLE,
  MOCK_THEME_IDS,
  MOCK_TILES,
  mockAboveClearance,
  mockBlockedReason,
  mockFieldByLabel,
  mockModelFieldReader,
  mockValueStat,
  toMockModelFieldOption,
  toMockModelSplitFieldOption,
  toMockSplitFieldOption,
  type MockPerson,
  MOCK_MODEL_RELEASE_ID,
} from "./distributions.mockData";
import {
  buildMockRun,
  freezeMockRunSending,
  mockFileName,
  mockSliceDetail,
  mockSplitCheck,
  mockSummary,
  projectMockRun,
  type BuildRunInput,
  type MockRunRecord,
} from "./distributionsMockRuns";
import { distinctTargets, emailDomain, mockSliceExtras, mockTestOnlyError, mockTestTargetsError, withTestTargets } from "./distributionsMockPreview";
import { audienceOf } from "./distributionCopyMappers";
import { mockConflictError, mockNotFoundError, mockValidationError } from "../mockDomainError";
import { resetBrandKitsMock } from "./distributionBrandKits.mock";
import { mockMasterLookFor, resetMastersMock } from "./distributionMasters.mock";
import {
  DEFAULT_AUDIENCE,
  LIMITS,
  MAX_MODEL_COLUMNS,
  MAX_MODEL_CONDITION_VALUES,
  MAX_MODEL_CONDITIONS,
  type ModelColumnApi,
  type ModelFieldListApi,
  type ModelRecipientSourceApi,
  type ModelRowsApi,
  type ModelRowsRequestApi,
  type AudienceApi,
  type CheckApi,
  type ChecksApi,
  type DistributionApi,
  type DistributionContentApi,
  type DistributionCreateApi,
  type DistributionListApi,
  type DistributionPatchApi,
  type DistributionScheduleApi,
  type DistributionStatusRequestApi,
  type DistributionSummaryApi,
  type EditStatusApi,
  type FormatApi,
  type RecipientApi,
  type RecipientInputApi,
  type RecipientPageApi,
  type RecipientSourceInfoApi,
  type RecipientsPutApi,
  type RowErrorDetailApi,
  type PreviewRenderApi,
  type PreviewRenderRequestApi,
  type RunAcceptedApi,
  type RetryAcceptedApi,
  type RunDetailApi,
  type RunListFiltersApi,
  type RunPageApi,
  type RunRequestApi,
  type RunSummaryApi,
  type SchedulePreviewApi,
  type SchedulePreviewRequestApi,
  type SignedFileApi,
  type SliceDetailApi,
  type SplitCheckApi,
  type SplitCheckRequestApi,
  type DistributionThemeApi,
  type SplitFieldApi,
  type SplitFieldListApi,
  type TemplateApi,
  type TemplateListApi,
  type TemplateWriteAcceptedApi,
  type TemplateWriteApi,
  type TokenListApi,
  type ValueStatsApi,
  type ValueStatsRequestApi,
  type WriteAcceptedApi,
} from "./distributions.types";
import { MOCK_READY_DISTRIBUTION_ID, MOCK_READY_DISTRIBUTION_NAME, MOCK_READY_RECIPIENT_COUNT, MOCK_SCALE_DISTRIBUTION_ID, MOCK_SCALE_DISTRIBUTION_NAME, MOCK_SCALE_FIELD_LABEL, buildMockScaleRows, mockScaleSource } from "./distributionsMockScale";
import { seedDistributionRunsAtScale } from "./distributionsMockRunsScale";
export { MOCK_READY_DISTRIBUTION_ID, MOCK_SCALE_DISTRIBUTION_ID } from "./distributionsMockScale";

/** Seeded with the four prototype distributions (any other id is seeded the same way). */
export const MOCK_DISTRIBUTION_STORY_ID = "story-northwind-weekly";
/** No distributions yet: the list's empty state. */
export const MOCK_EMPTY_STORY_ID = "story-no-distributions";
/** The tenant isn't on the new path: every route answers 404. */
export const MOCK_UNAVAILABLE_STORY_ID = "story-distribution-off";

export const MOCK_DISTRIBUTION_IDS = {
  weeklyRegional: "dist-weekly-regional",
  categoryBriefs: "dist-category-briefs",
  storeReview: "dist-store-review",
  holidayDraft: "dist-holiday-readiness",
  archivedBriefs: "dist-archived-briefs",
} as const;

export const MOCK_RUN_IDS = {
  regionalOct13: "run-regional-oct13",
  regionalOct6: "run-regional-oct6",
  categoryOct9: "run-category-oct9",
  storeOct1: "run-store-oct1",
  // ─── Round 6 · runs (WP3): every trigger in one history ───
  regionalTestOct18: "run-regional-test-oct18",
  regionalPreviewOct16: "run-regional-preview-oct16",
  regionalNowSep29: "run-regional-now-sep29",
  regionalFailedSep29: "run-regional-failed-sep29",
  // A run that is still sending, for the "Sending now" states.
  categorySendingOct20: "run-category-sending-oct20",
} as const;

/** Round 6 · runs (WP3): why the seeded failed run sent nothing. Neutral wording, shown verbatim. */
export const MOCK_FAILED_RUN_ERROR =
  "The data didn’t refresh. The data source didn’t answer, so this period’s numbers never loaded and nothing was sent. Check the connection in Settings › Data sources, then send it now.";

/** The caller's own address for test sends. */
export const MOCK_TEST_ADDRESS = "you@northwind.com";
/** The organisation's own email domains, from its members' addresses (the caller's, here). */
const MOCK_OWN_DOMAINS = [emailDomain(MOCK_TEST_ADDRESS)];
/** Whether the tenant has Slack connected; stories and tests switch it with setMockSlackConnected. */
let mockSlackConnected = true;
export const setMockSlackConnected = (connected: boolean): void => {
  mockSlackConnected = connected;
};

const LATENCY_MS = process.env.NODE_ENV === "test" ? 0 : 250;
const wait = (): Promise<void> => (LATENCY_MS ? new Promise((resolve) => setTimeout(resolve, LATENCY_MS)) : Promise.resolve());
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

interface MockDistributionRecord {
  dist: DistributionApi;
  rows: RecipientInputApi[];
  runs: MockRunRecord[];
  runsDone: number;
}

interface MockEdit {
  polls: number;
  error: EditStatusApi["error"];
}

interface MockStoryStore {
  distributions: Map<string, MockDistributionRecord>;
  edits: Map<string, MockEdit>;
  valueStats: Map<string, { polls: number; split: SplitFieldApi; audience: AudienceApi }>;
}

let seq = 0;
const nextId = (prefix: string): string => `${prefix}-${(++seq).toString(36)}-${Date.now().toString(36)}`;

const stories = new Map<string, MockStoryStore>();
let slackWithoutApp = new Set(MOCK_SLACK_CHANNELS_WITHOUT_APP);
let tenantTemplates: TemplateApi[] = [];
const templateEdits = new Map<string, MockEdit>();

const DEFAULT_CONTENT: DistributionContentApi = {
  template_id: "story",
  theme_id: null,
  formats: ["pdf", "pptx"],
  include: { files: true, link: false, message: true },
  include_ai_summary: true,
  include_snapshots: true,
  branding: MOCK_DEFAULT_BRAND,
  message: MOCK_NEW_DISTRIBUTION_MESSAGE,
};

/** Who the mock's writes are done by. */
const MOCK_ACTOR = { id: "user-hardik", name: "Hardik Chheda", email: null };

const EMPTY_CHECKS: ChecksApi = { blockers: [], warnings: [], last_test: null };

const baseDistribution = (storyId: string, id: string, name: string, split: SplitFieldApi | null, updatedAt: string): DistributionApi => ({
  id,
  story_id: storyId,
  name,
  status: "draft",
  split_label: split?.label ?? null,
  recipient_count: 0,
  channels: [],
  formats: DEFAULT_CONTENT.formats,
  template_name: "Story layout",
  schedule: null,
  next_run_at: null,
  last_run: null,
  updated_at: updatedAt,
  split,
  excluded_values: [],
  skip_empty_values: true,
  uncovered_policy: "hold",
  send_unfiltered_item_ids: [],
  recipient_source: null,
  recipient_summary: { total: 0, ready: 0, to_fix: 0, by_channel: {} },
  coverage: null,
  content: clone(DEFAULT_CONTENT),
  schedule_state: "none",
  schedule_error: null,
  runs_count: 0,
  run_count: 0,
  checks: EMPTY_CHECKS,
  permissions: { can_edit: true, can_run: true },
  created_by: { id: "user-hardik", name: "Hardik Chheda", email: null },
  created_at: updatedAt,
  activated_at: null,
  archived_at: null,
  archived_by_user_id: null,
  archived_by: null,
  paused_at: null,
  paused_by_user_id: null,
  paused_by: null,
});

const splitByLabel = (label: string): SplitFieldApi => MOCK_SPLIT_FIELDS.find((field) => field.label === label) ?? MOCK_SPLIT_FIELDS[0];

const weekly = (weekdays: number[], at: string, timezone: string, startsOn: string, ends: DistributionScheduleApi["ends"]): DistributionScheduleApi => ({
  kind: "weekly", every: 1, at, timezone, weekdays, day_of_month: null, quarter_month: null, starts_on: startsOn, ends,
});

const source = (fileName: string, uploadedAt: string): RecipientSourceInfoApi => ({
  kind: "list",
  file_name: fileName,
  uploaded_at: uploadedAt,
  matched_columns: { region: "value", full_name: "name", channel: "channel", address: "address", formats: "formats", team: "extra.team" },
});

const seedStory = (storyId: string): MockStoryStore => {
  const store: MockStoryStore = { distributions: new Map(), edits: new Map(), valueStats: new Map() };
  if (storyId === MOCK_EMPTY_STORY_ID) return store;
  const add = (record: MockDistributionRecord) => store.distributions.set(record.dist.id, record);

  const regional = baseDistribution(storyId, MOCK_DISTRIBUTION_IDS.weeklyRegional, "Weekly regional pack", splitByLabel("Region"), "2026-10-13T15:05:00Z");
  Object.assign(regional, {
    status: "active",
    schedule: weekly([1], "08:00", "America/Los_Angeles", "2026-09-22", { kind: "after", runs: 12 }),
    schedule_state: "synced",
    recipient_source: source("regional-pack-recipients.csv", "2026-09-20T17:12:00Z"),
    content: { ...clone(DEFAULT_CONTENT), message: clone(MOCK_DEFAULT_MESSAGE) },
    activated_at: "2026-09-20T17:20:00Z",
    created_at: "2026-09-18T16:00:00Z",
  });
  const regionalRecord: MockDistributionRecord = { dist: regional, rows: clone(MOCK_REGIONAL_PACK_ROWS), runs: [], runsDone: 4 };
  add(regionalRecord);

  const category = baseDistribution(storyId, MOCK_DISTRIBUTION_IDS.categoryBriefs, "Category buyer briefs", splitByLabel("Category"), "2026-10-10T12:00:00Z");
  Object.assign(category, {
    status: "paused",
    schedule: weekly([0, 1, 2, 3, 4], "07:00", "America/New_York", "2026-09-01", { kind: "never" }),
    schedule_state: "synced",
    recipient_source: source("category-buyers.csv", "2026-08-30T14:00:00Z"),
    content: { ...clone(DEFAULT_CONTENT), formats: ["pdf"] as FormatApi[] },
    activated_at: "2026-08-31T14:00:00Z",
    paused_at: new Date(Date.now() - 3 * 86_400_000).toISOString(),
    paused_by_user_id: "user-priya",
    paused_by: { id: "user-priya", name: "Priya Nair", email: null },
  });
  add({ dist: category, rows: clone(MOCK_CATEGORY_ROWS), runs: [], runsDone: 28 });

  const stores = baseDistribution(storyId, MOCK_DISTRIBUTION_IDS.storeReview, "Q3 store manager review", splitByLabel("Store"), "2026-10-01T16:15:00Z");
  Object.assign(stores, {
    status: "completed",
    schedule: { kind: "once", every: 1, at: "09:00", timezone: "America/Los_Angeles", weekdays: [], day_of_month: null, quarter_month: null, starts_on: "2026-10-01", ends: { kind: "never" } },
    schedule_state: "synced",
    recipient_source: source("store-managers.csv", "2026-09-28T18:00:00Z"),
    content: { ...clone(DEFAULT_CONTENT), formats: ["pdf"] as FormatApi[] },
    activated_at: "2026-09-28T18:30:00Z",
  });
  add({ dist: stores, rows: clone(MOCK_STORE_ROWS), runs: [], runsDone: 1 });

  const twoDaysAgo = new Date(Date.now() - 2 * 86_400_000).toISOString();
  add({ dist: baseDistribution(storyId, MOCK_DISTRIBUTION_IDS.holidayDraft, "Holiday readiness check", splitByLabel("District"), twoDaysAgo), rows: [], runs: [], runsDone: 0 });

  // An archived one (still readable, in the list only under Archived), and a paused one from before pauses were recorded.
  const archivedAt = new Date(Date.now() - 5 * 86_400_000).toISOString();
  const archivedDist = baseDistribution(storyId, MOCK_DISTRIBUTION_IDS.archivedBriefs, "Retired weekly digest", splitByLabel("Region"), archivedAt);
  Object.assign(archivedDist, {
    status: "archived",
    activated_at: "2026-07-01T14:00:00Z",
    archived_at: archivedAt,
    archived_by_user_id: MOCK_ACTOR.id,
    archived_by: { ...MOCK_ACTOR },
    permissions: { can_edit: false, can_run: false },
  });
  add({ dist: archivedDist, rows: [], runs: [], runsDone: 3 });

  // Until it was fixed after Oct 13, row 7 named a template that had been deleted, so its copy was held.
  // (A blank column never holds a copy: the variable is left blank and the copy still sends.)
  const HELD_FOR_MISSING_TEMPLATE = {
    7: "Held until it’s fixed: There’s no template called “intl-brief”. Choose one from the list, or pick Default to use the one from step 3.",
  };
  // Run history, built from the stored rows so deliveries and recipients agree.
  // At send time the Slack app was still in every channel, so South fails on delivery.
  const historicCtx = { ...ruleContext(regionalRecord), slackChannelsWithoutApp: new Set<string>() };
  regionalRecord.runs.push(
    // Round 6 · runs (WP3): a test and a preview after the last send, a hand-sent run and the failure before it.
    runFor(regionalRecord, MOCK_RUN_IDS.regionalTestOct18, "test", Date.parse("2026-10-18T16:20:00Z"), historicCtx, undefined, [1]),
    runFor(regionalRecord, MOCK_RUN_IDS.regionalPreviewOct16, "preview", Date.parse("2026-10-16T23:05:00Z"), historicCtx, undefined, [1, 5]),
    runFor(regionalRecord, MOCK_RUN_IDS.regionalOct13, "schedule", Date.parse("2026-10-13T15:00:04Z"), historicCtx, {
      failures: {
        3: "Bounced: mailbox full. Ask Priya to free up space, or change her address in Recipients, then retry.",
        6: "MagicBI was removed from #south-region on Oct 11. In Slack, open the channel and type /invite @MagicBI, then retry.",
      },
      held: HELD_FOR_MISSING_TEMPLATE,
    }),
    runFor(regionalRecord, MOCK_RUN_IDS.regionalOct6, "schedule", Date.parse("2026-10-06T15:00:03Z"), historicCtx, { held: HELD_FOR_MISSING_TEMPLATE }),
    runFor(regionalRecord, MOCK_RUN_IDS.regionalNowSep29, "now", Date.parse("2026-09-29T16:12:00Z"), historicCtx, { held: HELD_FOR_MISSING_TEMPLATE }),
    failedRunFor(regionalRecord, MOCK_RUN_IDS.regionalFailedSep29, Date.parse("2026-09-29T15:00:03Z"), historicCtx),
  );
  const categoryRecord = store.distributions.get(MOCK_DISTRIBUTION_IDS.categoryBriefs)!;
  categoryRecord.runs.push(runFor(categoryRecord, MOCK_RUN_IDS.categoryOct9, "schedule", Date.parse("2026-10-09T11:00:02Z")));
  categoryRecord.runs.unshift(freezeMockRunSending(runFor(categoryRecord, MOCK_RUN_IDS.categorySendingOct20, "schedule", Date.parse("2026-10-20T11:00:02Z"))));
  const storeRecord = store.distributions.get(MOCK_DISTRIBUTION_IDS.storeReview)!;
  storeRecord.runs.push(runFor(storeRecord, MOCK_RUN_IDS.storeOct1, "schedule", Date.parse("2026-10-01T16:00:05Z")));

  // People at scale: 220 recipients on a 192-value field (distributionsMockScale.ts).
  const scale = baseDistribution(storyId, MOCK_SCALE_DISTRIBUTION_ID, MOCK_SCALE_DISTRIBUTION_NAME, splitByLabel(MOCK_SCALE_FIELD_LABEL), "2026-10-12T17:30:00Z");
  Object.assign(scale, { recipient_source: mockScaleSource(MOCK_MODEL_RELEASE_ID) });
  const scaleValues = MOCK_FIELDS.find((field) => field.label === MOCK_SCALE_FIELD_LABEL)!.values;
  add({ dist: scale, rows: buildMockScaleRows(scaleValues), runs: [], runsDone: 0 });
  // A small draft with nothing to fix (Send now is enabled).
  const readyDraft = baseDistribution(storyId, MOCK_READY_DISTRIBUTION_ID, MOCK_READY_DISTRIBUTION_NAME, splitByLabel(MOCK_SCALE_FIELD_LABEL), "2026-10-12T17:45:00Z");
  Object.assign(readyDraft, { recipient_source: mockScaleSource(MOCK_MODEL_RELEASE_ID) });
  add({ dist: readyDraft, rows: buildMockScaleRows(scaleValues).slice(0, MOCK_READY_RECIPIENT_COUNT), runs: [], runsDone: 0 });
  // Runs at scale: five dist-scale-* distributions, one with 52 runs (distributionsMockRunsScale.ts).
  seedDistributionRunsAtScale(store);
  return store;
};

const storyStore = (storyId: string): MockStoryStore => {
  if (storyId === MOCK_UNAVAILABLE_STORY_ID) {
    throw mockNotFoundError("Personalized distribution isn’t turned on for this story. Ask your admin to turn it on.");
  }
  let store = stories.get(storyId);
  if (!store) {
    store = seedStory(storyId);
    stories.set(storyId, store);
  }
  return store;
};

/** `readArchived`: an archived distribution, its runs and its recipients stay readable (writes still refuse it). */
const recordFor = (storyId: string, distributionId: string, readArchived = false): MockDistributionRecord => {
  const record = storyStore(storyId).distributions.get(distributionId);
  if (!record || (record.dist.status === "archived" && !readArchived)) {
    throw mockNotFoundError("This distribution doesn’t exist anymore. It may have been deleted.");
  }
  return record;
};

function ruleContext(record: MockDistributionRecord): RecipientRuleContext {
  const field = mockFieldByLabel(record.dist.split?.label);
  return {
    fieldLabel: record.dist.split?.label ?? "value",
    fieldPlural: field?.plural,
    values: field?.values ?? [],
    excludedValues: record.dist.excluded_values,
    templateIds: [...MOCK_BUILT_IN_TEMPLATES, ...tenantTemplates].map((t) => t.id),
    themeIds: MOCK_THEME_IDS,
    rows: record.rows,
    slackChannelsWithoutApp: slackWithoutApp,
    // Round 6 (§4.1): copies for people inside stay inside the tenant's own domains.
    insideDomains: record.dist.content.audience === "inside" ? MOCK_INSIDE_DOMAINS : undefined,
  };
}

const coverageInput = (record: MockDistributionRecord) => {
  const field = mockFieldByLabel(record.dist.split?.label);
  return {
    values: field?.values ?? [],
    excludedValues: record.dist.excluded_values,
    emptyValues: field?.empty ?? [],
    skipEmptyValues: record.dist.skip_empty_values,
  };
};

function runFor(
  record: MockDistributionRecord,
  runId: string,
  trigger: RunRequestApi["trigger"] | "schedule",
  startedAtMs: number,
  ctx = ruleContext(record),
  staged: Pick<BuildRunInput, "failures" | "held"> = {},
  positions?: number[],
): MockRunRecord {
  const input = coverageInput(record);
  return buildMockRun({
    runId,
    trigger,
    dist: record.dist,
    rows: positions ? record.rows.filter((row) => positions.includes(row.position)) : record.rows,
    ctx,
    includedValues: includedValues(input),
    emptyValues: input.skipEmptyValues ? input.emptyValues : [],
    startedAtMs,
    testAddress: MOCK_TEST_ADDRESS,
    failures: staged.failures,
    held: staged.held,
  });
}

/** Round 6 · runs (WP3): a scheduled run that stopped at the data check: no copies, nothing sent. */
function failedRunFor(record: MockDistributionRecord, runId: string, startedAtMs: number, ctx = ruleContext(record)): MockRunRecord {
  const run = runFor(record, runId, "schedule", startedAtMs, ctx);
  run.detail.slices = [];
  run.detail.deliveries = [];
  Object.assign(run.detail.run, {
    status: "failed",
    error: MOCK_FAILED_RUN_ERROR,
    counts: { copies: 0, sent: 0, failed: 0, skipped: 0, held: 0 },
    finished_at: new Date(startedAtMs + 31 * 60_000).toISOString(),
    data_as_of: null,
  });
  return run;
}

/** Round 6 · runs (WP3): runs that send to recipients, as the list's "Last run" and run_count count them. */
const isSendRun = (run: MockRunRecord): boolean => run.detail.run.trigger === "now" || run.detail.run.trigger === "schedule";

const recipientsOf = (record: MockDistributionRecord): RecipientApi[] => {
  const ctx = ruleContext(record);
  return record.rows.map((row, i) => toRecipientApi(row, i, ctx, `${record.dist.id}-r${row.position}`));
};

const ISSUE_PARTS: [string, (n: number, field: string) => string][] = [
  ["invalid_email", (n) => pluralize(n, "email address", "email addresses")],
  ["missing_address", (n) => pluralize(n, "missing address", "missing addresses")],
  ["invalid_slack_channel", (n) => pluralize(n, "Slack channel name", "Slack channel names")],
  ["invalid_teams_webhook", (n) => pluralize(n, "Teams webhook link", "Teams webhook links")],
  ["channel_coming_soon", (n) => `${pluralize(n, "channel that’s", "channels that are")} coming soon`],
  ["value_not_in_data", (n, field) => `${n} ${n === 1 ? "value that isn’t a" : "values that aren’t a"} ${field}`],
  ["missing_value", (n, field) => `${n} missing ${field}`],
  ["slack_app_not_in_channel", (n) => pluralize(n, "Slack invite", "Slack invites")],
  ["duplicate", (n) => pluralize(n, "duplicate", "duplicates")],
  ["missing_name", (n) => pluralize(n, "missing name", "missing names")],
  ["invalid_cc", (n) => pluralize(n, "cc address", "cc addresses")],
  ["outside_address", (n) => pluralize(n, "address outside your organisation", "addresses outside your organisation")],
];

/** Blockers and warnings, in the prototype's step 5 wording. */
const checksOf = (record: MockDistributionRecord, recipients: RecipientApi[]): ChecksApi => {
  const { dist } = record;
  const blockers: CheckApi[] = [];
  const warnings: CheckApi[] = [];
  const field = dist.split?.label ?? "value";
  const coverage = computeCoverage({ ...coverageInput(record), rows: recipients });
  if (!dist.split) blockers.push({ code: "no_split", message: "Choose a field to split by.", step: 1 });
  else if (coverage.state === "known" && !coverage.total_values) blockers.push({ code: "no_values", message: "Keep at least one value to split by.", step: 1 });
  const toFix = recipients.filter((r) => r.status === "fix");
  if (!recipients.length) {
    blockers.push({ code: "no_recipients", message: "No one is added yet. Add at least one recipient.", step: 2 });
  } else if (toFix.length) {
    const counts: Record<string, number> = {};
    toFix.forEach((r) => r.issues.filter((i) => i.severity === "error").forEach((i) => (counts[i.code] = (counts[i.code] ?? 0) + 1)));
    const parts = ISSUE_PARTS.filter(([code]) => counts[code]).map(([code, text]) => text(counts[code], field));
    const other = Object.entries(counts).filter(([code]) => !ISSUE_PARTS.some(([known]) => known === code)).reduce((sum, [, n]) => sum + n, 0);
    if (other) parts.push(pluralize(other, "other problem", "other problems"));
    blockers.push({ code: "recipients_to_fix", message: `${toFix.length} recipient ${toFix.length === 1 ? "row needs" : "rows need"} a fix: ${andList(parts)}.`, step: 2 });
  } else if (!recipients.some((r) => coverage.by_value.some((v) => v.value === r.value && v.state !== "excluded"))) {
    blockers.push({ code: "no_copies", message: "No copy has a recipient yet. Add someone in step 2 to activate.", step: 2 });
  }
  const missingApp = Array.from(new Set(recipients.filter((r) => r.issues.some((i) => i.code === "slack_app_not_in_channel")).map((r) => r.address.trim())));
  if (missingApp.length) {
    blockers.push({ code: "slack_app_not_in_channel", message: `MagicBI isn’t in ${andList(missingApp)} yet. In Slack, open the channel and type /invite @MagicBI, then click Recheck.`, step: 2 });
  }
  const message = dist.content.message;
  const texts = [message.email_subject, message.email_body, message.slack_text];
  const extras = Array.from(new Set(record.rows.flatMap((row) => Object.keys(row.extra))));
  const unknown = findUnknownTokens(texts, extras, mockKpiNames());
  if (unknown.length) {
    blockers.push({ code: "unknown_tokens", message: `${andList(unknown.map((t) => `{{${t}}}`))} can’t be filled in, so ${unknown.length === 1 ? "it blocks" : "they block"} activation. Pick a variable from Insert variable in Message, or add the column to your recipient file.`, step: 3 });
  }
  if (!dist.content.include.files && !dist.content.include.message) {
    blockers.push({ code: "nothing_to_send", message: "Nothing goes out. Turn on the files in Content or the message in Message.", step: 3 });
  } else if (dist.content.include.files && !dist.content.formats.length) {
    blockers.push({ code: "no_formats", message: "Choose PDF, PowerPoint or both in Content.", step: 3 });
  }
  if (dist.schedule_state === "error" && dist.schedule_error) blockers.push({ code: "schedule_error", message: dist.schedule_error, step: 4 });
  if (coverage.uncovered.length) warnings.push({ code: "uncovered_values", message: uncoveredSentence(coverage.uncovered, dist.uncovered_policy), step: 2 });
  usedExtraTokens(texts, extras).forEach((token) => {
    const blank = record.rows.filter((row) => !row.extra[token]?.trim());
    if (!blank.length) return;
    const who = blank.length === 1 ? blank[0].name || "1 recipient" : `${blank.length} recipients`;
    warnings.push({ code: "blank_token", message: `{{${token}}} is blank for ${who}, so it’s left blank in ${blank.length === 1 ? "their copy" : "their copies"}. Add the value to your recipient file, or remove the variable.`, step: 2 });
  });
  const noted = recipients.filter((r) => r.status === "ready" && r.issues.length);
  if (noted.length) {
    warnings.push({ code: "recipient_notes", message: `${pluralize(noted.length, "recipient row sends", "recipient rows send")} with a fallback, such as the default theme. Review ${noted.length === 1 ? "it" : "them"} in step 2.`, step: 2 });
  }
  return { blockers, warnings, last_test: dist.checks.last_test };
};

/** The stored distribution with every server-computed field filled in. */
const hydrate = (record: MockDistributionRecord): DistributionApi => {
  const recipients = recipientsOf(record);
  const { dist } = record;
  const template = [...MOCK_BUILT_IN_TEMPLATES, ...tenantTemplates].find((t) => t.id === dist.content.template_id);
  const latest = record.runs.find(isSendRun);
  const nextRun = dist.status === "active" && dist.schedule ? nextSendIso(dist.schedule, Date.now(), record.runsDone) : null;
  return clone({
    ...dist,
    // Like api-service: content saved without an audience reads as the default.
    // Round 6 (FE-items): excluded_item_ids reads back as [] when never set, as api-service answers.
    content: { ...dist.content, audience: dist.content.audience ?? DEFAULT_AUDIENCE, excluded_item_ids: dist.content.excluded_item_ids ?? [] },
    recipient_count: recipients.length,
    channels: Array.from(new Set(recipients.map((r) => r.channel))),
    formats: dist.content.formats,
    template_name: template?.name ?? null,
    next_run_at: nextRun,
    last_run: latest ? runSummaryOf(latest) : null,
    recipient_summary: summarizeRecipients(recipients),
    coverage: dist.split ? computeCoverage({ ...coverageInput(record), rows: recipients }) : null,
    runs_count: record.runs.filter(isSendRun).length,
    run_count: record.runs.filter(isSendRun).length,
    checks: checksOf(record, recipients),
    permissions: dist.status === "archived" ? { can_edit: false, can_run: false } : dist.permissions,
  });
};

function runSummaryOf(record: MockRunRecord): RunSummaryApi {
  const { run } = projectMockRun(record);
  return { id: run.id, trigger: run.trigger, status: run.status, scheduled_for: run.scheduled_for, started_at: run.started_at, finished_at: run.finished_at, counts: run.counts, requested_by: run.requested_by, queued_at: run.queued_at ?? null };
}

const toSummary = (dist: DistributionApi): DistributionSummaryApi => ({
  id: dist.id,
  story_id: dist.story_id,
  name: dist.name,
  status: dist.status,
  split_label: dist.split_label,
  recipient_count: dist.recipient_count,
  channels: dist.channels,
  formats: dist.formats,
  template_name: dist.template_name,
  schedule: dist.schedule,
  next_run_at: dist.next_run_at,
  last_run: dist.last_run,
  updated_at: dist.updated_at,
  run_count: dist.run_count,
  archived_at: dist.archived_at ?? null,
  archived_by_user_id: dist.archived_by_user_id ?? null,
  archived_by: dist.archived_by ?? null,
  paused_at: dist.paused_at ?? null,
  paused_by_user_id: dist.paused_by_user_id ?? null,
  paused_by: dist.paused_by ?? null,
});

const accept = (storyId: string, distributionId: string, error: EditStatusApi["error"] = null): WriteAcceptedApi => {
  const requestId = nextId("req");
  storyStore(storyId).edits.set(requestId, { polls: 0, error });
  return { distribution_id: distributionId, request_id: requestId };
};

const touch = (record: MockDistributionRecord): void => {
  record.dist.updated_at = new Date().toISOString();
};

const validateName = (name: string | undefined): void => {
  if (name === undefined) return;
  const trimmed = name.trim();
  if (!trimmed) throw mockValidationError("Add a distribution name to continue.", [{ position: 0, field: "name", code: "required", message: "Add a distribution name to continue." }]);
  if (trimmed.length > 120) throw mockValidationError("Use 120 characters or fewer for the name.", [{ position: 0, field: "name", code: "too_long", message: "Use 120 characters or fewer for the name." }]);
};

// ─── Routes ──────────────────────────────────────────────────────────────────

export const mockListDistributions = async (storyId: string, scope: "current" | "archived" = "current"): Promise<DistributionListApi> => {
  await wait();
  const store = storyStore(storyId);
  return {
    items: Array.from(store.distributions.values())
      .filter((record) => (record.dist.status === "archived") === (scope === "archived"))
      .map((record) => toSummary(hydrate(record))),
  };
};

export const mockCreateDistribution = async (storyId: string, body: DistributionCreateApi): Promise<WriteAcceptedApi> => {
  await wait();
  validateName(body.name);
  const store = storyStore(storyId);
  const now = new Date().toISOString();
  const dist = baseDistribution(storyId, nextId("dist"), body.name.trim(), body.split ?? null, now);
  store.distributions.set(dist.id, { dist, rows: [], runs: [], runsDone: 0 });
  return accept(storyId, dist.id);
};

export const mockGetEditStatus = async (storyId: string, requestId: string): Promise<EditStatusApi> => {
  await wait();
  const edit = storyStore(storyId).edits.get(requestId);
  if (!edit) throw mockNotFoundError("We couldn’t find that change. Refresh the page to see the latest version.");
  edit.polls += 1;
  if (edit.polls === 1) return { request_id: requestId, status: "pending", error: null };
  return { request_id: requestId, status: edit.error ? "failed" : "done", error: edit.error };
};

export const mockSplitCheckRoute = async (storyId: string, body: SplitCheckRequestApi): Promise<SplitCheckApi> => {
  await wait();
  storyStore(storyId);
  return mockSplitCheck(body.split.label);
};

export const mockRequestValueStats = async (storyId: string, body: ValueStatsRequestApi): Promise<{ request_id: string }> => {
  await wait();
  const requestId = nextId("stats");
  storyStore(storyId).valueStats.set(requestId, { polls: 0, split: body.split, audience: body.audience });
  return { request_id: requestId };
};

export const mockGetValueStats = async (storyId: string, requestId: string): Promise<ValueStatsApi> => {
  await wait();
  const entry = storyStore(storyId).valueStats.get(requestId);
  if (!entry) throw mockNotFoundError("Those numbers aren’t available anymore. Pick the field again to reload them.");
  entry.polls += 1;
  if (entry.polls === 1) return { request_id: requestId, status: "pending", as_of: null, values: [], error: null };
  const field = mockFieldByLabel(entry.split.label);
  if (!field) {
    return { request_id: requestId, status: "failed", as_of: null, values: [], error: { code: "values_unavailable", message: `We couldn’t list the values of ${entry.split.label}. Try again, or pick another field.` } };
  }
  if (mockAboveClearance(field, entry.audience)) {
    return { request_id: requestId, status: "failed", as_of: null, values: [], error: { code: "SPLIT_FIELD_MASKED", message: mockBlockedReason(field, entry.audience) } };
  }
  return { request_id: requestId, status: "done", as_of: "2026-10-13T06:00:00-07:00", values: field.values.map((value) => mockValueStat(field.label, value)), error: null };
};

const scheduleProblem = (schedule: DistributionScheduleApi): string | null => {
  if (!/^\d{2}:\d{2}$/.test(schedule.at)) return "Pick a time for the send.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(schedule.starts_on)) return schedule.kind === "once" ? "Pick a date for the send." : "Pick a start date.";
  if (schedule.kind !== "once" && (!Number.isInteger(schedule.every) || schedule.every < 1 || schedule.every > 52)) return "Enter a whole number from 1 to 52.";
  if (schedule.kind === "weekly" && !schedule.weekdays.length) return "Pick at least one day of the week.";
  // magic-core 2.37.0 DistributionSchedule._shape: monthly and quarterly need exactly one of
  // day_of_month and monthly_rule, quarterly needs quarter_month.
  const rule = schedule.monthly_rule ?? null;
  const monthly = schedule.kind === "monthly" || schedule.kind === "quarterly";
  if (rule && !monthly) return "A weekday or business-day rule only applies to monthly and quarterly schedules.";
  if (monthly && rule && schedule.day_of_month !== null) return "Choose a day of the month or a weekday rule, not both.";
  if (monthly && !rule && schedule.day_of_month === null) return "Choose a day of the month, a weekday or a business day.";
  if (rule?.kind === "weekday" && !(Number.isInteger(rule.weekday) && rule.weekday >= 0 && rule.weekday <= 6)) return "Pick a day of the week.";
  if (schedule.kind === "quarterly" && schedule.quarter_month === null) return "Pick the month of the quarter.";
  if (schedule.ends.kind === "after" && (!Number.isInteger(schedule.ends.runs) || schedule.ends.runs < 1)) return "Enter a number of sends, 1 or more.";
  if (schedule.kind !== "once" && schedule.ends.kind === "on" && schedule.ends.date < schedule.starts_on) return "The end date is before the start date. Pick a later end date.";
  return null;
};

export const mockSchedulePreview = async (storyId: string, body: SchedulePreviewRequestApi): Promise<SchedulePreviewApi> => {
  await wait();
  storyStore(storyId);
  const problem = scheduleProblem(body.schedule);
  if (problem) throw mockValidationError(problem);
  return previewSchedule(body.schedule, { count: body.count, runsDone: body.runs_done ?? 0, fromMs: Date.now() });
};

export const mockGetDistribution = async (storyId: string, distributionId: string): Promise<DistributionApi> => {
  await wait();
  return hydrate(recordFor(storyId, distributionId, true));
};

export const mockPatchDistribution = async (storyId: string, distributionId: string, body: DistributionPatchApi): Promise<WriteAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  validateName(body.name);
  const { dist } = record;
  if (body.name !== undefined) dist.name = body.name.trim();
  if (body.split !== undefined) {
    dist.split = body.split;
    dist.split_label = body.split.label;
  }
  if (body.excluded_values !== undefined) dist.excluded_values = [...body.excluded_values];
  if (body.skip_empty_values !== undefined) dist.skip_empty_values = body.skip_empty_values;
  if (body.uncovered_policy !== undefined) dist.uncovered_policy = body.uncovered_policy;
  if (body.send_unfiltered_item_ids !== undefined) dist.send_unfiltered_item_ids = [...body.send_unfiltered_item_ids];
  if (body.content !== undefined) dist.content = clone(body.content);
  touch(record);
  return accept(storyId, distributionId);
};

export const mockDeleteDistribution = async (storyId: string, distributionId: string): Promise<WriteAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  Object.assign(record.dist, {
    status: "archived",
    schedule: null,
    schedule_state: "none",
    archived_at: new Date().toISOString(),
    archived_by_user_id: MOCK_ACTOR.id,
    archived_by: { ...MOCK_ACTOR },
    paused_at: null,
    paused_by_user_id: null,
    paused_by: null,
  });
  touch(record);
  return accept(storyId, distributionId);
};

/** Like api-service: an archived distribution comes back paused if it was ever activated, else as a draft. */
export const mockRestoreDistribution = async (storyId: string, distributionId: string): Promise<WriteAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId, true);
  if (record.dist.status !== "archived") throw mockConflictError("Only an archived distribution can be restored.");
  Object.assign(record.dist, {
    status: record.dist.activated_at ? "paused" : "draft",
    archived_at: null,
    archived_by_user_id: null,
    archived_by: null,
  });
  touch(record);
  return accept(storyId, distributionId);
};

const activationBlock = (checks: ChecksApi, toFix: number, total: number): string => {
  if (!total) return "Add at least one recipient in step 2 to activate.";
  if (toFix) return `Fix ${toFix} recipient ${toFix === 1 ? "row" : "rows"} to activate, or remove ${toFix === 1 ? "it" : "them"} in step 2.`;
  return checks.blockers[0]?.message ?? "Fix the checks in step 5 to activate.";
};

export const mockSetDistributionStatus = async (storyId: string, distributionId: string, body: DistributionStatusRequestApi): Promise<WriteAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  const current = record.dist.status;
  if (body.status === "active") {
    if (current !== "draft" && current !== "paused") throw mockConflictError(`This distribution is ${current}, so it can’t be activated.`);
    const hydrated = hydrate(record);
    if (hydrated.checks.blockers.length) {
      throw mockConflictError(activationBlock(hydrated.checks, hydrated.recipient_summary.to_fix, hydrated.recipient_summary.total));
    }
    Object.assign(record.dist, { status: "active", activated_at: new Date().toISOString(), paused_at: null, paused_by_user_id: null, paused_by: null });
  } else {
    if (current !== "active") throw mockConflictError("Only an active distribution can be paused.");
    Object.assign(record.dist, { status: "paused", paused_at: new Date().toISOString(), paused_by_user_id: MOCK_ACTOR.id, paused_by: { ...MOCK_ACTOR } });
  }
  touch(record);
  return accept(storyId, distributionId);
};

export const mockPutSchedule = async (storyId: string, distributionId: string, body: DistributionScheduleApi | null): Promise<WriteAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  if (body) {
    const problem = scheduleProblem(body);
    if (problem) throw mockValidationError(problem, [{ position: 0, field: "schedule", code: "invalid", message: problem }]);
  }
  record.dist.schedule = body ? clone(body) : null;
  record.dist.schedule_state = body ? "synced" : "none";
  record.dist.schedule_error = null;
  touch(record);
  return accept(storyId, distributionId);
};

export const mockGetRecipients = async (storyId: string, distributionId: string, offset = 0, limit: number = LIMITS.maxRecipients): Promise<RecipientPageApi> => {
  await wait();
  const record = recordFor(storyId, distributionId, true);
  const all = recipientsOf(record);
  return { items: all.slice(offset, offset + limit), total: all.length, offset, limit, summary: summarizeRecipients(all) };
};

export const mockPutRecipients = async (storyId: string, distributionId: string, body: RecipientsPutApi): Promise<WriteAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  const modelProblem = mockModelSourceProblem(body.source.kind, body.source.model ?? null);
  if (modelProblem) throw mockValidationError(modelProblem);
  if (body.rows.length > LIMITS.maxRecipients) {
    throw mockValidationError(`You can send to up to ${LIMITS.maxRecipients} people in one distribution. This list has ${body.rows.length}. Split it into two distributions.`);
  }
  const wide = body.rows.filter((row) => Object.keys(row.extra).length > LIMITS.maxExtraColumns);
  if (wide.length) {
    const details: RowErrorDetailApi[] = wide.map((row) => ({ position: row.position, field: "extra", code: "too_many_columns", message: `Row ${row.position} has more than ${LIMITS.maxExtraColumns} extra columns. Remove the ones your message doesn’t use.` }));
    throw mockValidationError(`Keep extra columns to ${LIMITS.maxExtraColumns} or fewer. Remove the ones your message doesn’t use.`, details);
  }
  record.rows = clone(body.rows);
  record.dist.recipient_source = { ...clone(body.source), uploaded_at: new Date().toISOString() };
  touch(record);
  return accept(storyId, distributionId);
};

/** Proposed route: re-runs the live checks. The mock assumes the owner invited the Slack app. */
export const mockRecheckRecipients = async (storyId: string, distributionId: string): Promise<WriteAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  record.rows.filter((row) => row.channel === "slack").forEach((row) => slackWithoutApp.delete(row.address.trim().toLowerCase()));
  return accept(storyId, distributionId);
};

export const mockStartRun = async (storyId: string, distributionId: string, body: RunRequestApi): Promise<RunAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  if (record.runs.some((run) => run.live && run.polls < 3 && run.detail.run.trigger === "now")) {
    throw mockConflictError("A run is already going. Wait for it to finish, then run it again.");
  }
  if (body.trigger === "now" && record.dist.status === "draft") {
    throw mockConflictError("Activate this distribution before you run it. You can send a test from step 3.");
  }
  if (body.trigger !== "now" && (!body.positions?.length || body.positions.length > 5)) {
    throw mockValidationError("Choose one to five recipients to test or preview.");
  }
  // Round 6 (FE-preview): test targets, and each copy's messages and brand.
  if (body.test_targets?.length && body.trigger !== "test") throw mockTestOnlyError();
  const targets = body.trigger === "test" ? body.test_targets ?? [] : [];
  const targetError = mockTestTargetsError(targets, {
    slackConnected: mockSlackConnected,
    audience: audienceOf(record.dist.content) ?? DEFAULT_AUDIENCE,
    ownDomains: MOCK_OWN_DOMAINS,
  });
  if (targetError) throw targetError;
  const runId = nextId("run");
  let run = runFor(record, runId, body.trigger, Date.now(), undefined, undefined, body.positions);
  if (targets.length) run = withTestTargets(run, distinctTargets(targets), slackWithoutApp);
  const runRows = body.positions ? record.rows.filter((row) => body.positions?.includes(row.position)) : record.rows;
  Object.assign(
    run,
    mockSliceExtras(run, record.dist, runRows, {
      storyTitle: MOCK_STORY_TITLE,
      values: mockFieldByLabel(record.dist.split?.label)?.values ?? [],
      summaryFor: (value) => mockSummary(run.fieldLabel, value),
      now: Date.now(),
    }),
  );
  run.live = true;
  record.runs.unshift(run);
  if (body.trigger === "now") record.runsDone += 1;
  if (body.trigger === "test") {
    const first = record.rows.find((row) => row.position === body.positions?.[0]);
    const sentTo = targets.length ? targets.map((target) => target.address).join(", ") : MOCK_TEST_ADDRESS;
    record.dist.checks = { ...record.dist.checks, last_test: { sent_at: new Date().toISOString(), value: first?.value ?? "", sent_to: sentTo } };
  }
  return { run_id: runId };
};

/** Retry only the failed deliveries of a run: 409 while a run goes, or when none failed. */
export const mockRetryFailed = async (storyId: string, distributionId: string, runId: string): Promise<RetryAcceptedApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  const failed = runRecord(storyId, distributionId, runId).detail.deliveries.filter((d) => d.status === "failed").length;
  if (record.runs.some((run) => run.live && run.polls < 3 && run.detail.run.trigger === "now")) {
    throw mockConflictError("A send of this distribution is running: wait for it to finish.");
  }
  if (!failed) throw mockConflictError("Nothing in this run can be sent again as it is: change the recipients first.");
  const { run_id } = await mockStartRun(storyId, distributionId, { trigger: "now" });
  return { run_id, delivery_count: failed };
};

/** Round 6 · runs (WP3): every trigger, newest first, with the `trigger` and `status` filters. */
export const mockListRuns = async (storyId: string, distributionId: string, offset = 0, limit = 20, filters: RunListFiltersApi = {}): Promise<RunPageApi> => {
  await wait();
  const record = recordFor(storyId, distributionId, true);
  const runs = record.runs
    .map(runSummaryOf)
    .filter((run) => !filters.trigger?.length || filters.trigger.includes(run.trigger))
    .filter((run) => !filters.status?.length || filters.status.includes(run.status));
  return { items: runs.slice(offset, offset + limit), total: runs.length, offset, limit };
};

const runRecord = (storyId: string, distributionId: string, runId: string): MockRunRecord => {
  const run = recordFor(storyId, distributionId, true).runs.find((r) => r.detail.run.id === runId);
  if (!run) throw mockNotFoundError("This run isn’t here anymore. Run history is kept for 90 days.");
  return run;
};

export const mockGetRun = async (storyId: string, distributionId: string, runId: string): Promise<RunDetailApi> => {
  await wait();
  const run = runRecord(storyId, distributionId, runId);
  if (run.live) run.polls += 1;
  return projectMockRun(run);
};

export const mockGetSlice = async (storyId: string, distributionId: string, runId: string, sliceId: string): Promise<SliceDetailApi> => {
  await wait();
  const detail = mockSliceDetail(runRecord(storyId, distributionId, runId), sliceId);
  if (!detail) throw mockNotFoundError("This copy isn’t here anymore. Files from each run are kept for 90 days.");
  return detail;
};

export const mockGetRunFilesZip = async (
  storyId: string,
  distributionId: string,
  runId: string,
  format: "all" | FormatApi,
): Promise<{ blob: Blob; filename: string }> => {
  await wait();
  const run = runRecord(storyId, distributionId, runId);
  const names = run.detail.slices.flatMap((slice) =>
    slice.files.filter((f) => format === "all" || f.format === format).map((f) => `${slugOf(slice.value)}/${mockFileName(slice.value, f.format)}`),
  );
  if (!names.length) throw mockNotFoundError("This file isn’t here anymore. Files from each run are kept for 90 days.");
  return {
    // A readable stand-in: the mock has no file store.
    blob: new Blob([`Mock zip. Files: ${names.join(", ")}`], { type: "application/zip" }),
    filename: `${slugOf(distributionId)}-${(run.detail.run.started_at ?? run.detail.run.queued_at ?? "").slice(0, 10)}.zip`,
  };
};

export const mockGetSliceFile = async (storyId: string, distributionId: string, runId: string, sliceId: string, format: FormatApi): Promise<SignedFileApi> => {
  await wait();
  const detail = mockSliceDetail(runRecord(storyId, distributionId, runId), sliceId);
  const file = detail?.slice.files.find((f) => f.format === format);
  if (!detail || !file) throw mockNotFoundError("This file isn’t here anymore. Files from each run are kept for 90 days.");
  const filename = mockFileName(detail.slice.value, format);
  return {
    // A readable stand-in: the mock has no file store.
    url: `data:text/plain;charset=utf-8,${encodeURIComponent(`Mock file: ${filename}`)}`,
    expires_at: new Date(Date.now() + 15 * 60_000).toISOString(),
    filename,
  };
};

// ─── Round 6 · brand kits (WP7): the kits store lives in its own file ───
export * from "./distributionBrandKits.mock";

// ─── Round 7 · uploaded PowerPoint templates (pd7 FE-master): the uploads store lives in its own file ───
export * from "./distributionMasters.mock";

// ─── Templates (tenant-wide) ─────────────────────────────────────────────────

const validateTemplate = (body: TemplateWriteApi): void => {
  const details: RowErrorDetailApi[] = [];
  if (!body.name.trim()) details.push({ position: 0, field: "name", code: "required", message: "Add a name for the template." });
  if (body.name.trim().length > 120) details.push({ position: 0, field: "name", code: "too_long", message: "Use 120 characters or fewer for the name." });
  if (body.source !== "pptx_master" && !body.slides_spec.length) details.push({ position: 0, field: "slides_spec", code: "required", message: "Add at least one slide." });
  if (body.source === "pptx_master" && !body.master_upload_id) details.push({ position: 0, field: "master_upload_id", code: "required", message: "Upload a PowerPoint master first." });
  // Round 7 (pd7 §2.4): the upload must be one of the tenant's, and the template a deck.
  if (body.source === "pptx_master" && body.master_upload_id && !mockMasterLookFor(body.master_upload_id)) details.push({ position: 0, field: "master_upload_id", code: "not_found", message: "We couldn’t find that PowerPoint file. Upload it again." });
  if (body.source === "pptx_master" && body.kind !== "deck") details.push({ position: 0, field: "kind", code: "invalid", message: "A template from your PowerPoint file is a slide deck." });
  if (details.length) throw mockValidationError(details[0].message, details);
};

const acceptTemplate = (templateId: string): TemplateWriteAcceptedApi => {
  const requestId = nextId("tpl-req");
  templateEdits.set(requestId, { polls: 0, error: null });
  return { template_id: templateId, request_id: requestId };
};

/** Round 7: an uploaded PowerPoint template's read carries the upload's look as it is now. */
const withMasterLook = (template: TemplateApi): TemplateApi =>
  template.source === "pptx_master" ? { ...template, look: mockMasterLookFor(template.master_id) } : template;

/** Non-archived distributions per template id (content template or any row's), across every story (api-service counts per tenant). */
const templateUsage = (): Map<string, number> => {
  const counts = new Map<string, number>();
  // The tenant's usage includes the default story's seeded distributions even before anything read them.
  storyStore(MOCK_DISTRIBUTION_STORY_ID);
  stories.forEach((store) =>
    store.distributions.forEach(({ dist, rows }) => {
      if (dist.status === "archived") return;
      // Each distribution counts once per template: the content's, plus any recipient row's own.
      const ids = new Set([dist.content.template_id, ...rows.map((row) => row.template_id)]);
      ids.forEach((id) => id && counts.set(id, (counts.get(id) ?? 0) + 1));
    }),
  );
  return counts;
};

export const mockListTemplates = async (): Promise<TemplateListApi> => {
  await wait();
  const usage = templateUsage();
  return clone({ items: [...MOCK_BUILT_IN_TEMPLATES, ...tenantTemplates.map(withMasterLook)].map((template) => ({ ...template, used_by: usage.get(template.id) ?? 0 })) });
};

const masterFields = (body: TemplateWriteApi): Pick<TemplateApi, "master_id" | "master_file_name"> => {
  const master = body.source === "pptx_master" ? mockMasterLookFor(body.master_upload_id) : null;
  return { master_id: master?.master_id ?? null, master_file_name: master?.file_name ?? null };
};

export const mockCreateTemplate = async (body: TemplateWriteApi): Promise<TemplateWriteAcceptedApi> => {
  await wait();
  validateTemplate(body);
  const id = nextId("tpl");
  tenantTemplates.push({
    id,
    name: body.name.trim(),
    kind: body.kind,
    source: body.source,
    description: body.source === "pptx_master" ? "Your PowerPoint template, filled with each person’s numbers" : `${pluralize(body.slides_spec.length, body.kind === "memo" ? "section" : "slide", body.kind === "memo" ? "sections" : "slides")} you set up`,
    slide_count: body.kind === "memo" ? 1 : body.slides_spec.length,
    slides_spec: clone(body.slides_spec),
    ...masterFields(body),
    created_by: { id: "user-hardik", name: "Hardik Chheda", email: null },
    updated_at: new Date().toISOString(),
  });
  return acceptTemplate(id);
};

export const mockUpdateTemplate = async (templateId: string, body: TemplateWriteApi): Promise<TemplateWriteAcceptedApi> => {
  await wait();
  if (MOCK_BUILT_IN_TEMPLATES.some((t) => t.id === templateId)) throw mockConflictError("Built-in templates can’t be changed. Make a new template instead.");
  const template = tenantTemplates.find((t) => t.id === templateId);
  if (!template) throw mockNotFoundError("This template doesn’t exist anymore. It may have been deleted.");
  validateTemplate(body);
  Object.assign(template, { name: body.name.trim(), kind: body.kind, source: body.source, slides_spec: clone(body.slides_spec), slide_count: body.kind === "memo" ? 1 : body.slides_spec.length, ...masterFields(body), updated_at: new Date().toISOString() });
  return acceptTemplate(templateId);
};

export const mockDeleteTemplate = async (templateId: string): Promise<TemplateWriteAcceptedApi> => {
  await wait();
  if (MOCK_BUILT_IN_TEMPLATES.some((t) => t.id === templateId)) throw mockConflictError("Built-in templates can’t be deleted.");
  if (!tenantTemplates.some((t) => t.id === templateId)) throw mockNotFoundError("This template doesn’t exist anymore. It may have been deleted.");
  tenantTemplates = tenantTemplates.filter((t) => t.id !== templateId);
  return acceptTemplate(templateId);
};

export const mockGetTemplateEdit = async (requestId: string): Promise<EditStatusApi> => {
  await wait();
  const edit = templateEdits.get(requestId);
  if (!edit) throw mockNotFoundError("We couldn’t find that change. Refresh the page to see the latest templates.");
  edit.polls += 1;
  return { request_id: requestId, status: edit.polls === 1 ? "pending" : "done", error: null };
};

// Round 6 · step 1 (FE-items): the list carries each audience's clearance for the caller.
export const mockListSplitFields = async (storyId: string, audience: AudienceApi): Promise<SplitFieldListApi> => {
  await wait();
  storyStore(storyId);
  return {
    items: MOCK_FIELDS.map((field) => toMockSplitFieldOption(field, audience)),
    clearances: { ...MOCK_AUDIENCE_CLEARANCE },
  };
};

/** The distribution themes: the presentation-themes fixture's keys, so stories and tests agree. */
export const MOCK_DISTRIBUTION_THEMES: DistributionThemeApi[] = MOCK_PRESENTATION_THEMES.themes.map((theme) => ({
  id: theme.key,
  name: theme.label,
  description: null,
  tone: theme.tone ?? "light",
}));

export const mockListDistributionThemes = async (): Promise<DistributionThemeApi[]> => {
  await wait();
  return clone(MOCK_DISTRIBUTION_THEMES);
};

// ─── Round 6 · message variables (FE-gallery) ────────────────────────────────

/** The KPI name rule: a KPI tile's title, trimmed; a tile with no title has no token. */
function mockKpiNames(): string[] {
  return MOCK_TILES.filter((tile) => tile.kind === "kpi" && tile.title.trim()).map((tile) => tile.title.trim());
}

/** GET …/{distribution_id}/tokens: the built-ins, the stored rows' extra keys and the KPI tiles. */
export const mockGetTokens = async (storyId: string, distributionId: string): Promise<TokenListApi> => {
  await wait();
  const record = recordFor(storyId, distributionId);
  const catalog = localTokenCatalog(record.rows.flatMap((row) => Object.keys(row.extra)), mockKpiNames());
  return clone({ builtin: [...catalog.builtin], columns: [...catalog.columns], kpis: [...catalog.kpis] });
};

/** Test and story helper: forget every write and reseed on next use. */
export const resetDistributionsMock = (): void => {
  stories.clear();
  resetBrandKitsMock();
  resetMastersMock();
  tenantTemplates = [];
  templateEdits.clear();
  slackWithoutApp = new Set(MOCK_SLACK_CHANNELS_WITHOUT_APP);
  mockSlackConnected = true;
  seq = 0;
  modelRowRequests.clear();
};

/** Every field the mock knows, for stories that render a field picker. */
export const MOCK_FIELD_LABELS = MOCK_FIELDS.map((field) => field.label);

// ─── Round 6 · WP5 recipients from the data model (FE-recipients) ─────────────

/** The tenant's own domains: the distinct email domains of its active members (§4.1). */
const MOCK_INSIDE_DOMAINS: ReadonlySet<string> = new Set(MOCK_MEMBER_EMAILS.map(emailDomainOf).filter(Boolean));

const modelRowRequests = new Map<string, { polls: number; storyId: string; result: Omit<ModelRowsApi, "request_id" | "status"> }>();

const TARGET_PATTERN = /^(match|value|name|channel|address|cc|formats|template_id|theme_id|brand\.(name|logo_url|accent|mark)|extra\.[a-z0-9_]{1,64})$/;

/** What's wrong with a request's columns and conditions (magic-core `ReadModelRows`), or null. */
const modelColumnsProblem = (columns: readonly ModelColumnApi[], where: ModelRowsRequestApi["where"] = []): string | null => {
  if (!columns.length || columns.length > MAX_MODEL_COLUMNS) return `Map between 1 and ${MAX_MODEL_COLUMNS} columns.`;
  const targets = columns.map((column) => column.target);
  if (targets.some((target) => !TARGET_PATTERN.test(target))) return "One of the columns has a target a recipient row doesn’t have.";
  if (new Set(targets).size !== targets.length) return "Two columns fill the same recipient field. Choose a different column for one of them.";
  if (where.length > MAX_MODEL_CONDITIONS) return `Use up to ${MAX_MODEL_CONDITIONS} filters.`;
  if (where.some((condition) => !condition.values.length || condition.values.length > MAX_MODEL_CONDITION_VALUES)) {
    return `Each filter needs between 1 and ${MAX_MODEL_CONDITION_VALUES} values.`;
  }
  return null;
};

/** The recipients PUT's `source` check (magic-core `RecipientSourceInfo` and `ModelRecipientSource`). */
const mockModelSourceProblem = (kind: "list" | "model", model: ModelRecipientSourceApi | null): string | null => {
  if (!model) return kind === "model" ? "A data model source needs its columns. Choose them again under From data model." : null;
  const problem = modelColumnsProblem(model.columns, model.where);
  if (problem) return problem;
  const targets = model.columns.map((column) => column.target);
  if (targets.includes("match")) return "A data model source can’t keep a match column.";
  if (!targets.includes("value") || !targets.includes("address")) return "Map the columns for the value and the address before importing.";
  return null;
};

const personValue = (person: MockPerson, key: keyof MockPerson | null): string | null => {
  if (!key) return null;
  const value = String(person[key] ?? "").trim();
  return value || null;
};

/** Distinct rows of the mapped attributes, filtered by `where`, masked at the caller's clearance. */
const readMockModelRows = (body: ModelRowsRequestApi): Omit<ModelRowsApi, "request_id" | "status"> | { failure: string } => {
  const readers = body.columns.map((column) => ({ target: column.target, reader: mockModelFieldReader(column.field.model_field_id) }));
  if (readers.some((entry) => !entry.reader)) return { failure: "One of the columns isn’t in the Story’s model anymore. Choose it again." };
  const tables = Array.from(new Set(readers.map((entry) => entry.reader?.table).filter((table): table is string => Boolean(table))));
  if (tables.length > 1) {
    return { failure: `${tables.join(" and ")} aren’t connected in the model, so their columns can’t be read together. Choose columns from one table.` };
  }
  const people = MOCK_MODEL_PEOPLE.filter((entry) => !tables.length || entry.table === tables[0]);
  const conditions = (body.where ?? []).map((condition) => ({ reader: mockModelFieldReader(condition.field.model_field_id), values: condition.values }));
  const kept = people.filter((entry) =>
    conditions.every(({ reader, values }) => reader !== null && values.includes(personValue(entry, reader.key) ?? "")),
  );
  const masked = readers.filter((entry) => entry.reader?.masked).map((entry) => entry.target);
  const seen = new Set<string>();
  const rows: Record<string, string | null>[] = [];
  kept.forEach((entry) => {
    const row = Object.fromEntries(
      readers.map(({ target, reader }) => [target, reader?.masked ? null : personValue(entry, reader?.key ?? null)]),
    );
    const key = JSON.stringify(row);
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  });
  const limit = body.limit ?? 1_000;
  return { rows: rows.slice(0, limit), masked, truncated: rows.length > limit, error: null };
};

export const mockListModelFields = async (storyId: string): Promise<ModelFieldListApi> => {
  await wait();
  storyStore(storyId);
  return clone({
    items: [...MOCK_MODEL_ATTRIBUTES.map(toMockModelFieldOption), ...MOCK_FIELDS.map(toMockModelSplitFieldOption)],
    clearance: MOCK_CALLER_CLEARANCE,
  });
};

export const mockRequestModelRows = async (storyId: string, body: ModelRowsRequestApi): Promise<{ request_id: string }> => {
  await wait();
  storyStore(storyId);
  const problem = modelColumnsProblem(body.columns, body.where);
  if (problem) throw mockValidationError(problem);
  if (body.limit !== undefined && (body.limit < 1 || body.limit > 5_000)) throw mockValidationError("Ask for between 1 and 5,000 rows.");
  const read = readMockModelRows(body);
  const requestId = nextId("model-rows");
  modelRowRequests.set(requestId, {
    polls: 0,
    storyId,
    result: "failure" in read ? { rows: [], masked: [], truncated: false, error: { code: "MODEL_ROWS_FAILED", message: read.failure } } : read,
  });
  return { request_id: requestId };
};

/** Like value stats: the first poll is `pending`, then `done` (or `failed` with the reason). */
export const mockGetModelRows = async (storyId: string, requestId: string): Promise<ModelRowsApi> => {
  await wait();
  storyStore(storyId);
  const entry = modelRowRequests.get(requestId);
  if (!entry || entry.storyId !== storyId) throw mockNotFoundError("We couldn’t find that read of your data model. Try again.");
  entry.polls += 1;
  if (entry.polls === 1) return { request_id: requestId, status: "pending", rows: [], masked: [], truncated: false, error: null };
  return clone({ request_id: requestId, status: entry.result.error ? "failed" : "done", ...entry.result });
};

/** Run-free preview: builds through the mock run, then answers as a PreviewRenderApi (running once, then ready). */
const mockRenders = new Map<string, { storyId: string; distributionId: string; runId: string; polls: number }>();

const mockRenderAnswer = async (renderId: string): Promise<PreviewRenderApi> => {
  const render = mockRenders.get(renderId);
  if (!render) throw mockNotFoundError("This preview isn’t here anymore.");
  const run = await mockGetRun(render.storyId, render.distributionId, render.runId);
  render.polls += 1;
  const slice = run.slices[0];
  if (run.run.status === "failed" || slice?.status === "failed") {
    return { render_id: renderId, status: "failed", error: slice?.error || run.run.error || "This copy couldn’t be built. Try again.", detail: null, pdf_url: null, pdf_expires_at: null, files: [] };
  }
  if (!slice || run.run.status === "queued" || run.run.status === "running") {
    return { render_id: renderId, status: "running", error: null, detail: null, pdf_url: null, pdf_expires_at: null, files: [] };
  }
  const detail = await mockGetSlice(render.storyId, render.distributionId, render.runId, slice.id);
  const pdf = slice.files.some((f) => f.format === "pdf");
  return {
    render_id: renderId,
    status: "ready",
    error: null,
    detail: { ...detail, slice: { ...detail.slice, id: renderId } },
    pdf_url: pdf ? `data:text/plain;charset=utf-8,${encodeURIComponent("Mock preview PDF")}` : null,
    pdf_expires_at: pdf ? new Date(Date.now() + 15 * 60_000).toISOString() : null,
    files: slice.files.map((f) => {
      const filename = mockFileName(detail.slice.value, f.format);
      return { format: f.format, filename, size: f.size_bytes ?? 0, url: `data:text/plain;charset=utf-8,${encodeURIComponent(`Mock file: ${filename}`)}`, expires_at: new Date(Date.now() + 15 * 60_000).toISOString() };
    }),
  };
};

export const mockStartPreviewRender = async (storyId: string, distributionId: string, body: PreviewRenderRequestApi): Promise<PreviewRenderApi> => {
  const { run_id } = await mockStartRun(storyId, distributionId, { trigger: "preview", positions: [body.position], artifacts: body.artifacts ?? true });
  const renderId = `render-${run_id}`;
  mockRenders.set(renderId, { storyId, distributionId, runId: run_id, polls: 0 });
  return mockRenderAnswer(renderId);
};

export const mockGetPreviewRender = (_storyId: string, _distributionId: string, renderId: string): Promise<PreviewRenderApi> => mockRenderAnswer(renderId);
